/**
 * Login-screen server-status state machine (R48).
 *
 * The electron app checked the API at login with a retry loop, started the
 * local server over IPC and showed the global circle-progress while waiting.
 * The Tauri migration broke every link of that chain (getVersionCheck's tuid
 * short-circuit, the missing serverInitialize IPC arm, the never-matching
 * interceptor guard), so the behaviour now lives in one explicit, spec-pinned
 * state machine the login page renders ABOVE the LOG IN block:
 *
 *  - checking:   first contact / remote server still within the grace window.
 *  - starting:   the API is on THIS machine (localhost/127.0.0.1) and is not
 *                answering - the server start has been requested, wait for it.
 *                A local server never gives up: it keeps waiting until up.
 *  - restarting: a LOCAL server has stayed silent for LOCAL_REINIT_MS - the
 *                start request is issued AGAIN (and again every window) and
 *                the message tells the operator so (R48b owner feedback: the
 *                first one-shot start attempt could fail silently, e.g. a
 *                dismissed UAC prompt, and the page then waited forever).
 *  - down:       a REMOTE server has not answered for REMOTE_GIVE_UP_MS -
 *                tell the operator it is not available; retries continue.
 *  - up:         the API answered; login can proceed.
 *
 * Owner spec 2026-07-25: retry every 10 seconds; remote gives the "not
 * available" intimation after 3 minutes of trying and keeps retrying; local
 * re-attempts the service start every 3 minutes ("Server is restarting,
 * please wait..."). The shell side only issues a start when the STODA
 * service is actually STOPPED - a RUNNING or START_PENDING service is never
 * poked (see the serverInitialize arm in src-tauri/src/lib.rs).
 */

export type ServerStatusState = 'checking' | 'starting' | 'restarting' | 'down' | 'up';

/** Interval between reachability probes while the server is not up. */
export const SERVER_RETRY_MS = 10_000;

/** How long a REMOTE server may stay silent before the state flips to down. */
export const REMOTE_GIVE_UP_MS = 3 * 60_000;

/** How long a LOCAL server may stay silent before the start is re-requested. */
export const LOCAL_REINIT_MS = 3 * 60_000;

/** The API host counts as local when the resolved base URL targets this machine. */
export function isLocalApiUrl(apiUrl: string): boolean {
  return !!apiUrl && (apiUrl.includes('localhost') || apiUrl.includes('127.0.0.1'));
}

/**
 * State while the server is NOT answering. `elapsedMs` is the time since the
 * current outage began (first failed probe).
 */
export function nextServerStatus(isLocalApi: boolean, elapsedMs: number): ServerStatusState {
  if (isLocalApi) {
    return elapsedMs >= LOCAL_REINIT_MS ? 'restarting' : 'starting';
  }
  return elapsedMs >= REMOTE_GIVE_UP_MS ? 'down' : 'checking';
}

/**
 * R48b: whether THIS failed probe should (re-)fire the serverInitialize IPC.
 * Local API only; the first failure fires immediately, then again every
 * LOCAL_REINIT_MS while the outage lasts — so a start attempt that died
 * silently is retried instead of waited on forever. The shell side is
 * idempotent (start only when the service is STOPPED), so an extra request
 * during a slow-but-healthy start is a no-op.
 */
export function shouldRequestServerStart(isLocalApi: boolean, lastRequestAt: number | null, now: number): boolean {
  if (!isLocalApi) {
    return false;
  }
  return lastRequestAt === null || now - lastRequestAt >= LOCAL_REINIT_MS;
}

/** Operator-facing message per state (rendered through the translate pipe). */
export function serverStatusMessage(state: ServerStatusState): string {
  switch (state) {
    case 'starting':
      return 'Server is starting, please wait...';
    case 'restarting':
      return 'Server is restarting, please wait...';
    case 'down':
      return 'Server is not available. Retrying every 10 seconds...';
    case 'up':
      return 'Server online';
    default:
      return 'Checking server status...';
  }
}
